/* -------------------------------------------------------------------------- */
/*  Wright-Ryan – Sage 300 import (P/C record pairs)                            */
/* -------------------------------------------------------------------------- *
 *  - "*" header record                                                        *
 *  - Emits GC/GR staffing + line items (when a GCGR is loaded), then           *
 *    estimate line items (row-level total, ACCT Code + Cost Type sort fields)  *
 *    and markups (account code resolved from the markup name)                   *
 *  - Returns [stringLines, "txt"]                                             */
/* -------------------------------------------------------------------------- */

import { BudgetImportConfig } from "./framework/types";

export const wrightRyanConfig: BudgetImportConfig = {
  format: "sage",
  requires: { project: true, gcgr: true },
  sage: {
    ext: "txt",
    render: (ctx) => {
      const tabulatedEstimate = ctx.rows;
      const project = ctx.project!;
      const master: string[] = [];
      const meta = `*,"99999","${project.name}"`;
      master.push(meta);

      const formattedDate = ctx.dateNum;

      // GC/GR is its own datasource: the file carries staffing and line items only when the user
      // has loaded a GCGR, and leaves them out otherwise.
      if (ctx.gcgr) {
        const { staff: tabulatedGcStaffing, lineItems: tabulatedGcGrLineItems } = ctx.gcgr;
        for (const row of tabulatedGcStaffing) {
          const gcCostType = "L";
          const costCode = row?.["cost_code"];
          if (!costCode || costCode.length < 2) {
            const rec1 = `"P",,"${row["position_name"]}",,${formattedDate},,,`;
            master.push(rec1);
            const rec2 = `"C",,,"${gcCostType}",${formattedDate},,,${row["grand_total"].toFixed(2)}`;
            master.push(rec2);
          } else {
            const rec1 = `"P","${costCode}","${row["position_name"]}",,${formattedDate}`;
            master.push(rec1);
            const rec2 = `"C","${costCode}",,"${gcCostType}",${formattedDate},,,${row["grand_total"].toFixed(2)}`;
            master.push(rec2);
          }
        }
        for (const row of tabulatedGcGrLineItems) {
          let gcCostType;
          const costCode = row?.["cost_code"];
          if (costCode && costCode.length > 2) {
            switch (costCode.slice(-2, -1)) {
              case "1":
                gcCostType = "S";
                break;
              case "2":
                gcCostType = "L";
                break;
              case "3":
                gcCostType = "M";
                break;
              case "4":
                gcCostType = "E";
                break;
              case "5":
                gcCostType = "O";
                break;
              case "6":
                gcCostType = "O";
                break;
            }
          } else {
            gcCostType = "O"; // Default to "O" if no specific type is found
          }
          if (!costCode || costCode.length < 2) {
            const rec1 = `"P",,"${row["name"]}",,${formattedDate},,,`;
            master.push(rec1);
            const rec2 = `"C",,,"${gcCostType}",${formattedDate},,,${row["total"]}`;
            master.push(rec2);
          } else {
            const rec1 = `"P","${costCode}","${row["name"]}",,${formattedDate}`;
            master.push(rec1);
            const rec2 = `"C","${costCode}",,"${gcCostType}",${formattedDate},,,${row["total"]}`;
            master.push(rec2);
          }
        }
      }

      for (const row of tabulatedEstimate) {
        if (row.type === "line_item") {
          const accountCode = row?.["ACCT Code_code"];
          const costType = row?.["Cost Type_code"];
          if (!accountCode) {
            const rec1 = `"P",,"${row.name}",,${formattedDate},,,`;
            master.push(rec1);
            if (!costType) {
              const rec2 = `"C",,,,${formattedDate},,,${row["total"]}`;
              master.push(rec2);
            } else {
              const rec2 = `"C",,,"${costType}",${formattedDate},,,${row["total"]}`;
              master.push(rec2);
            }
          } else {
            const rec1 = `"P","${accountCode}","${row.name}",,${formattedDate}`;
            master.push(rec1);
            if (!costType) {
              const rec2 = `"C","${accountCode}",,,${formattedDate},,,${row["total"]}`;
              master.push(rec2);
            } else {
              const rec2 = `"C","${accountCode}",,"${costType}",${formattedDate},,,${row["total"]}`;
              master.push(rec2);
            }
          }
        }
        if (row.type === "markup") {
          let accountCode = "18.150.01"; // Default code for "All Other Fees"

          // Check for specific markup types (case insensitive)
          switch (row.name.toLowerCase()) {
            case "cm contingency":
              accountCode = "18.100.01";
              break;
            case "software technology":
              accountCode = "01.604.50";
              break;
            case "wrc insurance":
              accountCode = "01.806.01";
              break;
            case "p&p bond":
              accountCode = "01.805.01";
              break;
          }

          const rec1 = `"P","${accountCode}","${row.name}",,${formattedDate}`;
          master.push(rec1);
          const rec2 = `"C","${accountCode}",,,${formattedDate},,,${row["total"]}`;
          master.push(rec2);
        }
      }

      return master;
    },
  },
};
